// basho.dev front end. No framework: TypeScript modules, bundled into static/dist by esbuild
// (tools/build.mjs), checked with `npm run check` and the types gate (tools/types-gate.mjs).
import { $ } from "./dom.ts";
import { archived, wireFollow } from "./follow.ts";
import { makeTv, isTv } from "./tv.ts";
import { initLive } from "./live.ts";
import { initReplay } from "./replay.ts";
import { initPanel } from "./panel.ts";
import { syncPanel, wirePanelUi } from "./panelui.ts";
import { initForm, maskSecrets } from "./forms.ts";
import { initSound } from "./sound.ts";
import { initLook } from "./look.ts";
import { initNav } from "./nav.ts";
import { initStable } from "./stable.ts";
import { publishTimings } from "./timing.ts";
import { endBoutDwell, startBoutDwell, trackBoutOpen, trackSocialAction } from "./telemetry.ts";
import { markShow, wantStage } from "./show.ts";
import { wantMusic } from "./sound/music.ts";
import { initConsent } from "./consent.ts";
import { flag, onApp, onBroadcast } from "./env.ts";
import { follow, release } from "./broadcast.ts";
import { initOverlay } from "./overlay.ts";
import { initShareUI } from "./share.ts";
import { initSort } from "./sort.ts";

// the perf readout (?perf=1): its module fetched only then, as early as can be so it counts from the start
if (flag("perf")) import("./perf.ts").then((m) => m.mountPerf(document)).catch((e) => console.warn("perf", e));
// the cost dials (?cost=1): the stage's gl spend turned down live, to find what a weak gpu can carry
if (flag("cost"))
  import("./fx/costdials.ts").then((m) => m.mountCostDials(document)).catch((e) => console.warn("cost", e));

// the app's Steam presence (desktop/src/steam.rs): what the page shows, told to the app (the app only)
if (onApp()) import("./presence.ts").then((m) => m.initPresence()).catch((e) => console.warn("presence", e));
// the app's sign-in through the browser (src/applink.rs): its waiting page
if (document.querySelector("[data-app-link]"))
  import("./applink.ts").then((m) => m.initAppLink()).catch((e) => console.warn("applink", e));
// the app's report of what a bout said, in the gear (src/web/api/report.rs)
if (document.querySelector("[data-report-row]"))
  import("./report.ts").then((m) => m.initReport()).catch((e) => console.warn("report", e));
// the app's photo mode (P), over the stage (fx/photo.ts): its camera is the stage's, wherever one is
if (onApp()) import("./fx/photo.ts").then((m) => m.initPhoto()).catch((e) => console.warn("photo", e));
// the app left alone: the screensaver (fx/idle.ts)
if (onApp()) import("./fx/idle.ts").then((m) => m.initIdle()).catch((e) => console.warn("idle", e));
// the site's graphics in the gear: auto or a step pinned (fx/pace.ts); never the stream's, which keeps its URL's
if (document.querySelector("[data-pace]") && !onBroadcast())
  import("./fx/pace.ts").then((m) => m.wirePace(document)).catch((e) => console.warn("pace", e));
// the app's graphics sliders, in the gear (the app only: the site has none)
if (document.querySelector("[data-graphics]"))
  import("./fx/graphics.ts").then((m) => m.wireGraphics(document)).catch((e) => console.warn("graphics", e));

// the ring: live bout or replay. Re-run after a tv switch swaps in new markup.
// the full show (the stage, the crowd, the figures) lives behind a door: fetched the first time it is wanted, never on the classic ring
let show: typeof import("./fx/show.ts") | null = null;

function mountShow(any: HTMLElement) {
  if (show) {
    show.mountShow(any);
    return;
  }
  import("./fx/show.ts")
    .then((m) => {
      show = m;
      if (document.contains(any) && wantStage()) m.mountShow(any);
    })
    .catch((e) => console.warn("show", e)); // the bout may have changed while it loaded
}
/** the error page's scene (fx/crush.ts): fetched only there, mounted when the stage is wanted */
let crush: typeof import("./fx/crush.ts") | null = null;
function initCrush() {
  const any = $("article.crush");
  if (!any || !wantStage()) return;
  if (crush) {
    crush.mountCrush(any);
    return;
  }
  import("./fx/crush.ts")
    .then((m) => {
      crush = m;
      if (document.contains(any) && wantStage()) m.mountCrush(any);
    })
    .catch((e) => console.warn("crush", e));
}
function initRing() {
  initCrush();
  const any = $("article.bout");
  if (any) trackBoutOpen(any);
  startBoutDwell(any);
  initOverlay();
  if (any && wantStage()) mountShow(any); // the ring in space behind whatever is on: live, replay, or sealed — when chosen
  if (onApp() && any) void import("./intro.ts").then((m) => m.arm(any)); // the app's first-bout card, once (intro.ts)
  const pane = $("article.bout [data-transcript]");
  if (pane) {
    wireFollow(pane);
    pane.scrollTop = archived(pane) ? 0 : pane.scrollHeight; // a finished bout's page opens on its first turn (#628); a live one at its latest
  }
  const bout = $("article.bout[data-live]");
  initLive(bout, hooks);
  if (isTv() && !bout) tv.onStart(() => tv.switchNow()); // replay or empty ring: jump when a bout starts
  initReplay(hooks);
  syncPanel(); // the headings true to the viewer's folds
  if (isTv()) initPanel(); // after the players: the rows they took back are on the page again through them, so the card reads the truth
}
const tv = makeTv(initRing);
wirePanelUi(); // the panel's folds, grips and width: on the document, once
const hooks: TvHooks = { onBoutEnd: (hold) => tv.next(hold) };
publishTimings(document);
// destructive forms outside the live stable region confirm before posting (the stable
// region has its own handler in stable.js). Added once: it survives nav swaps.
document.addEventListener(
  "submit",
  (e) => {
    const f = e.target;
    if (
      f instanceof HTMLFormElement &&
      f.dataset.confirm &&
      !f.hasAttribute("data-stable-form") &&
      !confirm(f.dataset.confirm)
    )
      e.preventDefault();
  },
  true,
);
// a form that must post once (a mint spends money): its buttons go dead once the post is on its way
document.addEventListener("submit", (e) => {
  const f = e.target;
  if (!(f instanceof HTMLFormElement) || !f.hasAttribute("data-once") || e.defaultPrevented) return;
  setTimeout(() => f.querySelectorAll("button").forEach((b) => (b.disabled = true))); // after the post has taken its fields
});
// a select that is the whole form (the banzuke's model filter): changing it submits
document.addEventListener("change", (e) => {
  const s = e.target;
  if (s instanceof HTMLSelectElement && s.hasAttribute("data-autosubmit")) s.form?.requestSubmit();
});
document.addEventListener("click", (e) => {
  const el = e.target instanceof Element ? e.target.closest("[data-share], [data-rematch], .share, .rematch") : null;
  if (el) {
    if (el.hasAttribute("data-rematch") || el.classList.contains("rematch")) {
      trackSocialAction("rematch");
    } else if (el.hasAttribute("data-share") || el.classList.contains("share")) {
      trackSocialAction("share");
    }
  }
});

// social actions: follow, challenge
document.addEventListener("submit", (e) => {
  const f = e.target;
  if (f instanceof HTMLFormElement) {
    if (f.classList.contains("follow") || (f.action && f.action.includes("/follow"))) {
      trackSocialAction("follow");
    } else if (f.classList.contains("challenge") || (f.action && f.action.includes("/challenge"))) {
      const sel = f.querySelector("select[name='contender'], [data-model]");
      const model = sel instanceof HTMLSelectElement ? sel.value : null;
      trackSocialAction("challenge", model);
    }
  }
});
markShow(); // boot.js marked the stage wherever WebGL2 is named; where it still cannot be drawn, the flat ring stands
initRing();
initForm();
initSort();
maskSecrets(document);
let stable = initStable();
initSound();
initLook();
initConsent();
initShareUI();
// the band plays the live bout everywhere: away from the ring the broadcast feeds it, in the ring the replay does
const tuneIn = () => {
  if (isTv() || !wantMusic()) release();
  else follow();
};
tuneIn();
// the band's switch thrown (sound.js): the broadcast tunes in or out
document.addEventListener("basho:music", () => tuneIn());
// the show's switch thrown (look.js): the stage mounts over the flat ring, or is cleared and the ring stands again
document.addEventListener("basho:show", () => {
  const any = $("article.bout");
  if (wantStage()) {
    if (any) mountShow(any);
    initCrush();
  } else if (show) show.unmountShow();
});
// pages change under a fixed header: the ring's streams stop, the new page's pieces start
initNav({
  leave: () => {
    endBoutDwell();
    tv.leave();
    stable.leave();
  },
  arrive: () => {
    initRing();
    tuneIn();
    initForm();
    initSort();
    maskSecrets(document);
    stable = initStable();
    const main = document.querySelector("main");
    if (main) {
      initSound(main);
      initLook(main);
    }
  },
});
